import { Link } from 'react-router-dom';
import React, { useState, useEffect } from 'react';

const HowItWorksPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const steps = [
    {
      number: "1",
      title: "Setup Your Profile",
      description: "Enter your USMLE scores, graduation details, USCE experience, and visa requirements to create your personalized medical profile.",
      details: [
        "USMLE Step 1 and Step 2 CK scores",
        "Country of graduation and timeline", 
        "US Clinical Experience (USCE) months",
        "Visa sponsorship needs (J1 vs H1B)",
        "Specialty preferences and priorities"
      ],
      color: "from-blue-500 to-blue-600",
      icon: "👤"
    },
    {
      number: "2", 
      title: "Get Familiarity Scores",
      description: "Our algorithm calculates compatibility scores using %Non-US IMG data and position availability to rank programs by your match likelihood.",
      details: [
        "Analysis of %Non-US IMG acceptance rates",
        "Available positions per program",
        "Personalized compatibility scoring",
        "Profile-based program ranking",
        "Strategic match probability assessment"
      ],
      color: "from-teal-500 to-teal-600",
      icon: "🎯"
    },
    {
      number: "3",
      title: "Filter & Compare Programs",
      description: "Use smart filters based on your profile criteria and compare programs with detailed analytics, Step 2 cutoffs, and visa sponsorship info.",
      details: [
        "Profile-based intelligent filtering",
        "Step 2 CK cutoff requirements (Premium)",
        "Visa sponsorship compatibility",
        "USCE requirement matching",
        "Geographic and program type filters"
      ],
      color: "from-indigo-500 to-indigo-600",
      icon: "🔍"
    },
    {
      number: "4",
      title: "Apply Strategically",
      description: "Save favorites, analyze program composition, and make data-driven decisions about where to invest your application efforts and fees.",
      details: [
        "Save programs to favorites list",
        "Detailed program comparison views",
        "Program composition analytics",
        "Strategic application planning",
        "Cost-effective program selection"
      ],
      color: "from-purple-500 to-purple-600",
      icon: "⭐"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
<img 
  src="/images/logo.png" 
  alt="Residency Companion Logo" 
  className="w-10 h-10 mr-4 object-contain"
/>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                Residency Companion
              </h1>
            </Link>
            <div className="flex items-center space-x-1">
              <Link to="/login" className="text-gray-600 hover:text-gray-900 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:bg-gray-50">
                Sign In
              </Link>
              <Link to="/signup" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl">
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-teal-50">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob-enhanced"></div>
          <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-teal-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob-enhanced animation-delay-2000"></div>
        </div>
        
        <div className={`relative z-10 text-center px-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="max-w-4xl mx-auto">
            <div className="mb-6 inline-flex items-center space-x-2 bg-white/70 backdrop-blur-sm border border-gray-200 rounded-full px-4 py-2 shadow-sm">
              <span className="w-4 h-4 text-blue-600">✨</span>
              <span className="text-sm text-gray-700 font-medium">Strategic IMG Residency Application Process</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-thin text-gray-900 mb-6 leading-tight">
              How Residency Companion
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent font-light">
                Works For You
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto font-light leading-relaxed">
              From profile setup to strategic application planning - your complete guide to IMG residency matching
            </p>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-thin text-gray-900 mb-6">
              Your Journey to
              <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent font-light"> Residency Success</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Follow these four strategic steps to find and apply to the right residency programs
            </p>
          </div>

          <div className="space-y-20">
            {steps.map((step, index) => (
              <div key={index} className={`flex flex-col lg:flex-row items-center gap-12 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                {/* Content */}
                <div className={`flex-1 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`} style={{ transitionDelay: `${index * 200}ms` }}>
                  <div className="flex items-center mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-r ${step.color} rounded-full flex items-center justify-center mr-4 shadow-lg`}>
                      <span className="text-2xl font-bold text-white">{step.number}</span>
                    </div>
                    <h2 className="text-4xl font-semibold text-gray-900">{step.title}</h2>
                  </div>
                  
                  <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                    {step.description}
                  </p>

                  <div className="space-y-3">
                    {step.details.map((detail, detailIndex) => (
                      <div key={detailIndex} className="flex items-center">
                        <div className={`w-2 h-2 bg-gradient-to-r ${step.color} rounded-full mr-3`}></div>
                        <span className="text-gray-600">{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Visual */}
                <div className={`flex-1 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`} style={{ transitionDelay: `${index * 200 + 100}ms` }}>
                  <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-12 min-h-[300px] flex items-center justify-center">
                    <div className={`text-8xl opacity-20`}>{step.icon}</div>
                    <div className={`absolute inset-0 bg-gradient-to-r ${step.color} opacity-5 rounded-2xl`}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-thin text-gray-900 mb-6">
              Complete Process Timeline
            </h2>
            <p className="text-xl text-gray-600 font-light">
              From profile creation to strategic program selection
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-blue-600 to-teal-600 rounded-full"></div>
            
            {[
              'Profile Setup (5 minutes)', 
              'Familiarity Score Analysis (Instant)', 
              'Program Filtering & Comparison (10 minutes)',
              'Strategic Application Planning (Ongoing)'
            ].map((item, index) => (
              <div key={index} className="relative flex items-center mb-8">
                <div className="flex-1 text-right pr-8">
                  {index % 2 === 0 && (
                    <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-100">
                      <h3 className="font-semibold text-gray-900">{item}</h3>
                    </div>
                  )}
                </div>
                <div className="w-4 h-4 bg-blue-600 rounded-full relative z-10"></div>
                <div className="flex-1 text-left pl-8">
                  {index % 2 === 1 && (
                    <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-100">
                      <h3 className="font-semibold text-gray-900">{item}</h3>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features Highlight */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-thin text-gray-900 mb-6">
              What Makes Us
              <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent font-light"> Different</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">IMG-Specific Algorithm</h3>
              <p className="text-gray-600">Our familiarity scoring is designed specifically for International Medical Graduates using actual %Non-US IMG data.</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-gradient-to-r from-teal-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📊</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Real Program Data</h3>
              <p className="text-gray-600">Access to Step 2 CK cutoffs, visa sponsorship details, and actual acceptance rates for strategic planning.</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💡</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Strategic Application</h3>
              <p className="text-gray-600">Make data-driven decisions about where to invest your time and application fees for maximum success.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-teal-600">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-thin text-white mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl text-blue-100 mb-8 font-light">
            Join thousands of IMGs who use Residency Companion for strategic residency applications
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className="bg-white text-blue-600 px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Create Your Profile
            </Link>
            <Link
              to="/features"
              className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:bg-white hover:text-blue-600"
            >
              Explore Features
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-6">
<img 
  src="/images/logo.png" 
  alt="Residency Companion Logo" 
  className="w-12 h-12 mr-4 object-contain"
/>
                <div>
                  <h4 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                    Residency Companion
                  </h4>
                  <p className="text-sm text-gray-500 font-medium">IMG Residency Matching Platform</p>
                </div>
              </div>
              <p className="text-gray-600 font-light">
                Your strategic partner in medical residency applications.
              </p>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-4">Product</h5>
              <ul className="space-y-3 text-gray-600">
                <li><Link to="/features" className="hover:text-blue-600 transition-colors">Features</Link></li>
                <li><Link to="/how-it-works" className="text-blue-600 font-medium">How It Works</Link></li>
                <li><Link to="/api" className="hover:text-blue-600 transition-colors">API</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-4">Support</h5>
              <ul className="space-y-3 text-gray-600">
                <li><Link to="/help" className="hover:text-blue-600 transition-colors">Help Center</Link></li>
                <li><Link to="/contact" className="hover:text-blue-600 transition-colors">Contact Us</Link></li>
                <li><Link to="/refund-policy" className="hover:text-blue-600 transition-colors">Refund Policy</Link></li>
                <li><Link to="/cancellation-policy" className="hover:text-blue-600 transition-colors">Cancellation Policy</Link></li>
                <li><Link to="/privacy" className="hover:text-blue-600 transition-colors">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-blue-600 transition-colors">Terms</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-4">Company</h5>
              <ul className="space-y-3 text-gray-600">
                <li><Link to="/about" className="hover:text-blue-600 transition-colors">About</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 mt-12 pt-8 text-center">
            <p className="text-gray-500 text-sm">
              © 2025 Residency Companion. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes blob-enhanced {
          0% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
          25% { transform: translate(20px, -30px) scale(1.05) rotate(5deg); }
          50% { transform: translate(-15px, 10px) scale(0.95) rotate(-3deg); }
          75% { transform: translate(25px, -20px) scale(1.08) rotate(7deg); }
          100% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
        }
        .animate-blob-enhanced { animation: blob-enhanced 9s ease-in-out infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default HowItWorksPage;